import React, {useCallback} from 'react'
import {useSelector} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'
import {DistanceFrom} from 'components/DistanceFrom'
import {useIsStreetViewFeatureEnabled} from 'hooks/useIsStreetViewFeatureEnabled'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'
import {
  getDistanceFromTarget,
  getSearchType
} from 'modules/sapiSearch/selectors'
import {SearchType} from 'modules/sapiSearch/utils'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleButton} from '@vio/atlas/helpers/NoStyleButton'
import {Stack} from '@vio/atlas/helpers/Stack'
import {Icon} from '@vio/atlas/Icon'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {LocationSummaryLoading} from './LocationSummaryLoading'
import {
  LocationSummaryWrapper,
  MapLink,
  MobileWrapper,
  PlaceNameWrapper,
  StreetViewText,
  Wrapper
} from './styles'

interface Props {
  hotel?: ContentHotel
  onLocationSummaryClick: () => void
}

export const LocationSummary: React.FC<Props> = ({
  hotel,
  onLocationSummaryClick
}) => {
  const {isMobile} = useDeviceLayout()
  const showOverlay = useShowOverlay()
  const distanceFromTarget = useSelector(state =>
    getDistanceFromTarget(state, hotel?.objectID)
  )

  const searchType = useSelector(getSearchType)

  const isStreetViewEnabled = useIsStreetViewFeatureEnabled(hotel?.country)

  const handleStreetViewClick = useCallback(() => {
    showOverlay({
      type: OverlayType.StreetView,
      params: {
        hotelId: hotel.objectID
      }
    })
  }, [hotel?.objectID, showOverlay])

  if (!hotel) return <LocationSummaryLoading />

  if (isMobile) {
    return (
      <MobileWrapper>
        <LocationSummaryWrapper>
          <Stack space="s200">
            <NoStyleButton data-id="Location" onClick={onLocationSummaryClick}>
              <PlaceNameWrapper tag="span" variant="bodyXS">
                <Icon size="sm" name="Pin" />
                <span>
                  {hotel.placeDisplayName}
                  &nbsp;·&nbsp;
                  <MapLink>
                    <FormattedMessageWrapper id="map" defaultMessage="Map" />
                  </MapLink>
                </span>
              </PlaceNameWrapper>
            </NoStyleButton>
            {searchType !== SearchType.Area && Boolean(distanceFromTarget) && (
              <div>
                <PlaceNameWrapper tag="span" variant="bodyXS">
                  <DistanceFrom distance={distanceFromTarget} />
                </PlaceNameWrapper>
              </div>
            )}
          </Stack>
          {isStreetViewEnabled && isMobile && (
            <Box paddingLeft="s400">
              <Button
                size="sm"
                variant="tertiary"
                onClick={handleStreetViewClick}
                iconStart={<Icon name="Person360" />}
              >
                <StreetViewText variant="bodyXS">
                  <FormattedMessageWrapper
                    id="streetView.CTA"
                    defaultMessage="Street view"
                  />
                </StreetViewText>
              </Button>
            </Box>
          )}
        </LocationSummaryWrapper>
      </MobileWrapper>
    )
  }

  return (
    <Wrapper>
      <NoStyleButton data-id="Location" onClick={onLocationSummaryClick}>
        <PlaceNameWrapper tag="span" variant="bodyS">
          <Icon size="sm" name="Pin" />
          <span>{hotel.placeDisplayName}</span>
          <span>·</span>
          <MapLink>
            <FormattedMessageWrapper id="map" defaultMessage="Map" />
          </MapLink>
        </PlaceNameWrapper>
      </NoStyleButton>
      {searchType !== SearchType.Area && Boolean(distanceFromTarget) && (
        <div>
          <PlaceNameWrapper tag="span" variant="bodyXS">
            <DistanceFrom distance={distanceFromTarget} />
          </PlaceNameWrapper>
        </div>
      )}
    </Wrapper>
  )
}
